import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { Grid } from '../../engine/data/common/Grid';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { DicomImageSlice } from '../dicom/dicomImageSlice';
import { DicomSeries, Series } from '../dicom/series';
import {
    WindowLevel,
    WindowingPreset,
    computeAutoWindowLevelForCT,
    computeAutoWindowLevelFromHistogram,
    MR_AUTO_WL_ALGORITHMS,
} from './windowLevel';
import { Modality } from '../dicom/modality';
import { computeSUVbwFactor } from './petSuv';
import {
    Image3DOptions,
    InterpolationMethod,
    SliceInfo,
    findNearestSlices,
    getMaxConsecutiveGap,
    interpolateSlice,
} from './sliceInterpolation';

/**
 * Internal info for interpolation during pixel data population
 */
interface InterpolationInfo {
    /** Map of z-index to existing DICOM slice */
    sliceMap: Map<number, DicomImageSlice>;
    /** Total number of slices including interpolated ones */
    totalSlices: number;
    /** Interpolation method to use */
    interpolationMethod: InterpolationMethod;
}

/**
 * Validation status for slice position analysis
 */
export enum SliceValidationStatus {
    /** All slices present with uniform spacing - can reconstruct directly */
    CompleteReconstruction = 'CompleteReconstruction',
    /** Uniform spacing but some slices are missing - could interpolate */
    MissingSlices = 'MissingSlices',
    /** Non-uniform spacing that could be corrected via resampling */
    ResamplingNeeded = 'ResamplingNeeded',
    /** Cannot construct 3D image (no slices, not 3D, or other fundamental issues) */
    CannotConstruct = 'CannotConstruct',
}

/**
 * Information about a gap between consecutive slices
 */
export interface SliceGapInfo {
    /** The gap distance in mm */
    gap: number;
    /** SOP Instance UIDs of the two slices forming this gap */
    slicePair: string;
}

/**
 * Result of slice position validation
 */
export interface SliceValidationResult {
    /** Validation status */
    status: SliceValidationStatus;
    /** Reason for the validation status (empty if CompleteReconstruction) */
    reason: string;
    /** Expected z-resolution based on slice positions (undefined if cannot compute) */
    expectedZResolution: number | undefined;
    /** Slice z-projections sorted ascending: [z-projection along stack normal, sopInstanceUid] */
    sortedSliceZProjections: Array<[number, string]>;
    /** Image Position Patient (DICOM) of the bottom-most slice */
    bottomSlicePatientPosition: ReadonlyVec3;
    /** Z-direction normal vector */
    zDirection: ReadonlyVec3;
    /** Gaps that deviate from expected resolution */
    invalidGaps: SliceGapInfo[];
    /** Detected missing slice count (if MissingSlices status) */
    missingSliceCount: number;
}

/**
 * Finds the base (most likely original) slice spacing using statistical analysis.
 * Uses histogram/clustering to find the smallest gap that appears most frequently.
 *
 * The algorithm handles both:
 * - Missing slices (gaps that are integer multiples of base spacing)
 * - MR variations (slight deviations in slice spacing due to tilted acquisition)
 *
 * @param gaps Array of gap distances between consecutive slices
 * @param tolerance Tolerance for grouping similar gaps (absolute, in mm)
 * @returns The detected base spacing
 */
function findBaseSpacing(gaps: number[], tolerance: number): number {
    if (gaps.length === 0) {
        return 1.0;
    }

    if (gaps.length === 1) {
        return gaps[0];
    }

    // Sort gaps to help with clustering
    const sortedGaps = [...gaps].sort((a, b) => a - b);

    // Use adaptive tolerance for bucketing: at least the specified tolerance,
    // but also consider relative tolerance based on median gap (handles MR variations)
    const medianGap = sortedGaps[Math.floor(sortedGaps.length / 2)];
    const relativeTolerance = medianGap * 0.08; // 8% relative tolerance for clustering
    const bucketTolerance = Math.max(tolerance, relativeTolerance);

    // Group gaps into buckets using the adaptive tolerance
    const buckets: Array<{ representative: number; gaps: number[] }> = [];

    for (const gap of gaps) {
        // Find existing bucket within tolerance, using a fixed representative
        let foundBucket = false;
        for (const bucket of buckets) {
            if (Math.abs(gap - bucket.representative) <= bucketTolerance) {
                bucket.gaps.push(gap);
                foundBucket = true;
                break;
            }
        }
        if (!foundBucket) {
            // Use the first value added as the fixed representative during bucketing
            buckets.push({ representative: gap, gaps: [gap] });
        }
    }

    // After clustering, compute a stable representative (median) for each bucket
    for (const bucket of buckets) {
        const sortedBucketGaps = [...bucket.gaps].sort((a, b) => a - b);
        const medianIndex = Math.floor(sortedBucketGaps.length / 2);
        bucket.representative = sortedBucketGaps[medianIndex];
    }
    // Find the best base spacing using a scoring system
    // Score = count / representative (favors smaller gaps with high frequency)
    let bestSpacing = gaps[0];
    let bestScore = 0;

    for (const bucket of buckets) {
        const score = bucket.gaps.length / bucket.representative;
        if (score > bestScore) {
            bestScore = score;
            bestSpacing = bucket.representative;
        }
    }

    return bestSpacing;
}

/**
 * Validates slice positions for 3D image reconstruction.
 * Analyzes the spatial arrangement of DICOM slices to determine if they can form a valid 3D volume.
 *
 * Uses statistical gap analysis to correctly detect the base slice spacing even when
 * multiple slices are missing. This avoids the pitfall of calculating expected resolution
 * as totalDistance / (count - 1), which gives incorrect results when slices are missing.
 *
 * @param series The series containing DICOM image slices
 * @param tolerance Tolerance for slice spacing deviation in mm (default: 0.2mm)
 * @returns Validation result with status and detailed information
 */
export function validateSlicePositions(
    series: Series,
    tolerance: number = Image3D.SliceResolutionTolerance
): SliceValidationResult {
    const emptyResult: SliceValidationResult = {
        status: SliceValidationStatus.CannotConstruct,
        reason: '',
        expectedZResolution: undefined,
        sortedSliceZProjections: [],
        bottomSlicePatientPosition: vec3.create(),
        zDirection: vec3.create(),
        invalidGaps: [],
        missingSliceCount: 0,
    };

    const imageSlices = series.dicomImageSlices;

    // Check basic requirements
    if (imageSlices.length === 0) {
        return {
            ...emptyResult,
            reason: 'No image slices in series',
        };
    }

    if (!series.is3DImage) {
        return {
            ...emptyResult,
            reason: 'Series is not marked as a 3D image',
        };
    }

    // Single slice is always valid for reconstruction
    if (imageSlices.length === 1) {
        const slice = imageSlices[0];
        const zdir = vec3.fromValues(
            slice.imageOrientation[6],
            slice.imageOrientation[7],
            slice.imageOrientation[8]
        );
        return {
            status: SliceValidationStatus.CompleteReconstruction,
            reason: '',
            expectedZResolution:
                slice.sliceThickness > 0 ? slice.sliceThickness : 1.0,
            sortedSliceZProjections: [
                [vec3.dot(zdir, slice.imagePosition), slice.sopInstanceUid],
            ],
            bottomSlicePatientPosition: slice.imagePosition,
            zDirection: zdir,
            invalidGaps: [],
            missingSliceCount: 0,
        };
    }

    const exampleSlice = imageSlices[0];

    // Get z-direction from image orientation
    const zdir = vec3.fromValues(
        exampleSlice.imageOrientation[6],
        exampleSlice.imageOrientation[7],
        exampleSlice.imageOrientation[8]
    );

    // Calculate slice positions projected onto z-axis
    const slicePositions = new Array<[number, string]>();
    let bottomSlicePos: ReadonlyVec3 = vec3.create();
    let minz = Number.MAX_SAFE_INTEGER;

    for (const imageSlice of imageSlices) {
        const imagePos = imageSlice.imagePosition;
        const zcoord = vec3.dot(zdir, imagePos);

        slicePositions.push([zcoord, imageSlice.sopInstanceUid]);
        if (zcoord < minz) {
            bottomSlicePos = imagePos;
            minz = zcoord;
        }
    }

    slicePositions.sort((a, b) => a[0] - b[0]);

    // Calculate gaps between consecutive slices
    const gaps: SliceGapInfo[] = slicePositions.slice(1).map((pos, i) => ({
        gap: Math.abs(pos[0] - slicePositions[i][0]),
        slicePair: `${pos[1]} - ${slicePositions[i][1]}`,
    }));

    const gapValues = gaps.map((g) => g.gap);

    // Find the base spacing using statistical analysis
    // This correctly identifies the original slice spacing even when multiple slices are missing
    const baseSpacing = findBaseSpacing(gapValues, tolerance);

    // Use relative tolerance for MR compatibility (slight variations in slice spacing)
    // At least the absolute tolerance, but also 8% of base spacing for MR robustness
    const effectiveTolerance = Math.max(tolerance, baseSpacing * 0.08);

    // Validate all gaps against base spacing
    // A gap is valid if it's approximately N * baseSpacing where N >= 1
    let totalMissingSlices = 0;
    const invalidGaps: SliceGapInfo[] = [];

    for (const gapInfo of gaps) {
        const ratio = gapInfo.gap / baseSpacing;
        const nearestInt = Math.round(ratio);
        const expectedGap = nearestInt * baseSpacing;
        const deviation = Math.abs(gapInfo.gap - expectedGap);

        if (deviation > effectiveTolerance) {
            // Gap is not a multiple of base spacing - truly non-uniform
            invalidGaps.push(gapInfo);
        } else if (nearestInt > 1) {
            // Gap is N * baseSpacing where N > 1 - missing slices
            totalMissingSlices += nearestInt - 1;
        }
        // else: nearestInt === 1 and deviation <= tolerance - normal gap
    }

    // Classification based on analysis results
    if (invalidGaps.length === 0 && totalMissingSlices === 0) {
        // All gaps are uniform within tolerance - complete reconstruction
        return {
            status: SliceValidationStatus.CompleteReconstruction,
            reason: '',
            expectedZResolution: baseSpacing,
            sortedSliceZProjections: slicePositions,
            bottomSlicePatientPosition: bottomSlicePos,
            zDirection: zdir,
            invalidGaps: [],
            missingSliceCount: 0,
        };
    }

    if (invalidGaps.length === 0 && totalMissingSlices > 0) {
        // All gaps are multiples of base spacing - missing slices detected
        // Collect the gaps that indicate missing slices for reporting
        const missingSliceGaps = gaps.filter((gapInfo) => {
            const ratio = gapInfo.gap / baseSpacing;
            const nearestInt = Math.round(ratio);
            return nearestInt > 1;
        });

        return {
            status: SliceValidationStatus.MissingSlices,
            reason: `${totalMissingSlices} slice(s) appear to be missing`,
            expectedZResolution: baseSpacing,
            sortedSliceZProjections: slicePositions,
            bottomSlicePatientPosition: bottomSlicePos,
            zDirection: zdir,
            invalidGaps: missingSliceGaps,
            missingSliceCount: totalMissingSlices,
        };
    }

    // Gaps are non-uniform - resampling would be needed
    return {
        status: SliceValidationStatus.ResamplingNeeded,
        reason: `Non-uniform slice spacing detected. Offending slices: ${invalidGaps.map((g) => g.slicePair).join(', ')}`,
        expectedZResolution: baseSpacing,
        sortedSliceZProjections: slicePositions,
        bottomSlicePatientPosition: bottomSlicePos,
        zDirection: zdir,
        invalidGaps,
        missingSliceCount: 0,
    };
}

/**
 * {@link Image3D} is a container for 3D image information parsed from DICOM {@link DicomImageSlice} objects.
 */
export class Image3D {
    public static readonly SliceResolutionTolerance = 0.2; // mm
    /**
     * Maximum number of consecutive missing slices that will be automatically
     * interpolated when constructing a 3D volume.
     *
     * This limit is intentionally conservative to bound the axial distance over
     * which synthetic data are generated (for typical CT protocols this
     * corresponds to only a few millimetres). Beyond this range, internal
     * image‑quality evaluations showed a risk of degraded anatomical fidelity
     * and potentially misleading structures in interpolated regions.
     *
     * Gaps larger than this threshold are therefore treated as clinically
     * significant and must be handled explicitly by the workflow (e.g. by
     * rejecting the series or requiring user review), rather than being
     * silently auto‑filled by the software.
     *
     * The detailed justification, hazard analysis, and validation evidence for
     * this limit are maintained in the IEC 62304 design and risk management
     * documentation for slice interpolation.
     */
    public static readonly MaxAutoInterpolatedSlices = 5;

    /**
     * Create a {@link Image3D} from DICOM object, including the image slice data.
     * Only succeeds for series with complete, uniformly-spaced slices unless
     * interpolation is enabled via options.
     *
     * Use {@link validateSlicePositions} to check validity before calling this method.
     *
     * @param series {@link Series}
     * @param options Optional configuration for interpolation
     * @returns {@link Image3D} or null if series cannot form a 3D image
     * @throws Error if slice spacing is non-uniform or slices are missing (without interpolation enabled)
     */
    static fromSeries(
        series: Series,
        options?: Image3DOptions
    ): Image3D | null {
        const validation = validateSlicePositions(series);

        if (validation.status === SliceValidationStatus.CannotConstruct) {
            return null;
        }

        if (validation.status === SliceValidationStatus.MissingSlices) {
            if (options?.interpolateMissingSlices) {
                return Image3D.fromSeriesWithInterpolation(
                    series,
                    validation,
                    options
                );
            }
            throw new Error(
                `Missing slices detected: ${validation.reason}. ` +
                    `Offending gaps: ${validation.invalidGaps.map((g) => g.slicePair).join(', ')}`
            );
        }

        if (validation.status === SliceValidationStatus.ResamplingNeeded) {
            throw new Error(
                `Non-uniform slice spacing detected, this is not supported. ${validation.reason}`
            );
        }

        const imageSlices = series.dicomImageSlices;
        const exampleSlice = imageSlices[0];

        const resolution = vec3.fromValues(
            exampleSlice.pixelSpacing[0],
            exampleSlice.pixelSpacing[1],
            validation.expectedZResolution ?? 1.0
        );

        const size = vec3.fromValues(
            exampleSlice.cols,
            exampleSlice.rows,
            imageSlices.length
        );

        return new Image3D(
            Grid.fromDicom(
                size,
                resolution,
                validation.bottomSlicePatientPosition,
                exampleSlice.imageOrientation
            ),
            series.seriesInstanceUID,
            series.frameOfReferenceUid,
            exampleSlice,
            series
        );
    }

    /**
     * Create an Image3D with interpolation for missing slices.
     * This creates a volume with the full expected number of slices,
     * interpolating pixel data for any missing slices.
     */
    private static fromSeriesWithInterpolation(
        series: Series,
        validation: SliceValidationResult,
        options: Image3DOptions
    ): Image3D {
        if (
            validation.expectedZResolution === undefined ||
            validation.expectedZResolution <= 1e-4
        ) {
            throw new Error(
                `Cannot perform interpolation without valid z-resolution (${validation.expectedZResolution})`
            );
        }
        const imageSlices = series.dicomImageSlices;
        const exampleSlice = imageSlices[0];
        const zResolution = validation.expectedZResolution;

        // Calculate total slices including interpolated ones
        const totalSlices = imageSlices.length + validation.missingSliceCount;

        // Build a map of z-index -> slice for existing slices
        const minZ = validation.sortedSliceZProjections[0][0];
        const sliceMap = new Map<number, DicomImageSlice>();

        for (const imageSlice of imageSlices) {
            const zCoord = vec3.dot(
                validation.zDirection,
                imageSlice.imagePosition
            );
            const zIndex = Math.round((zCoord - minZ) / zResolution);
            sliceMap.set(zIndex, imageSlice);
        }

        // Check safety limits
        const existingIndices = Array.from(sliceMap.keys()).sort(
            (a, b) => a - b
        );
        const maxConsecutive = getMaxConsecutiveGap(
            existingIndices,
            totalSlices
        );
        const maxAllowed =
            options.maxInterpolatedSlices ?? Image3D.MaxAutoInterpolatedSlices;

        if (maxConsecutive > maxAllowed) {
            throw new Error(
                `Too many consecutive missing slices: ${maxConsecutive} ` +
                    `(max allowed: ${maxAllowed}). Consider increasing maxInterpolatedSlices if intentional.`
            );
        }

        const resolution = vec3.fromValues(
            exampleSlice.pixelSpacing[0],
            exampleSlice.pixelSpacing[1],
            zResolution
        );

        const size = vec3.fromValues(
            exampleSlice.cols,
            exampleSlice.rows,
            totalSlices
        );

        const image = new Image3D(
            Grid.fromDicom(
                size,
                resolution,
                validation.bottomSlicePatientPosition,
                exampleSlice.imageOrientation
            ),
            series.seriesInstanceUID,
            series.frameOfReferenceUid,
            exampleSlice,
            series,
            {
                sliceMap,
                totalSlices,
                interpolationMethod:
                    options.interpolationMethod ?? InterpolationMethod.Linear,
            }
        );

        return image;
    }

    /**
     * Update the pixel data from the DICOM slices in the series
     */
    private updatePixelDataFromSeries(): void {
        for (const slice of this.series.dicomImageSlices) {
            // determine the z-coordinate of the slice in voxel space
            const voxelCoords = this.grid.toVoxelSpace(slice.imagePosition);
            const sliceSize = this.grid.sizeX * this.grid.sizeY;
            // use Math.floor to get the slice index (DICOM image position is at the center of the slice)
            const zcoord = Math.floor(voxelCoords[2]);
            if (zcoord < 0 || zcoord >= this.grid.sizeZ) {
                throw new Error(
                    `Slice position outside of image volume (${slice.sopInstanceUid}, pos ${slice.imagePosition} - ${zcoord}}`
                );
            }
            const pixelIdx = zcoord * sliceSize;
            this.pixelData.set(slice.pixelData, pixelIdx);
        }
    }

    /**
     * Assemble Float32 volume directly from DICOM slice Float32 data.
     * Used for PET images to bypass Int16 intermediate entirely.
     */
    private updateFloatPixelDataFromSeries(): void {
        const totalVoxels = this.grid.sizeX * this.grid.sizeY * this.grid.sizeZ;
        this._floatPixelData = new Float32Array(totalVoxels);
        const sliceSize = this.grid.sizeX * this.grid.sizeY;

        for (const slice of this.series.dicomImageSlices) {
            const voxelCoords = this.grid.toVoxelSpace(slice.imagePosition);
            const zcoord = Math.floor(voxelCoords[2]);
            if (zcoord < 0 || zcoord >= this.grid.sizeZ) {
                throw new Error(
                    `Slice position outside of image volume ` +
                        `(${slice.sopInstanceUid}, ` +
                        `pos ${slice.imagePosition} — ${zcoord})`
                );
            }
            this._floatPixelData.set(slice.floatPixelData, zcoord * sliceSize);
        }
    }

    public hasSlice(sopInstanceUid: string): boolean {
        for (const slice of this.series.dicomImageSlices) {
            if (slice.sopInstanceUid === sopInstanceUid) {
                return true;
            }
        }
        return false;
    }

    /**
     * Find the SOP Instance UID of the slice at the given
     * normal position (projection of the slice center onto
     * the grid's Z unit vector, in mm). This is NOT necessarily
     * the same as the patient-space Z coordinate for non-HFS
     * orientations.
     */
    public getUidForSlice(normalPosition: number): string | undefined {
        const normalDir = this.grid.getUnitZ();
        const slice = this.series.dicomImageSlices.find(
            (slice) =>
                Math.abs(
                    vec3.dot(normalDir, slice.imagePosition) - normalPosition
                ) < 0.1
        );
        return slice?.sopInstanceUid ?? undefined;
    }

    public getAcquisitionWindowLevel(): WindowLevel | undefined {
        return this._acquisitionWindowLevel;
    }

    public getAutoWindowLevel(): WindowLevel | undefined {
        if (this.firstSlice.modality === Modality.CT) {
            return computeAutoWindowLevelForCT(
                this.floatPixelData,
                this.grid.sizeX,
                this.grid.sizeY,
                this.grid.sizeZ
            );
        }
        return computeAutoWindowLevelFromHistogram(
            this.floatPixelData,
            this.grid.sizeX,
            this.grid.sizeY,
            this.grid.sizeZ
        );
    }

    /**
     * Returns all MR-specific auto WL algorithm results as presets.
     * Only meaningful for MR modality; returns empty array for CT/PT.
     */
    public getMrAutoWindowLevelPresets(): WindowingPreset[] {
        if (this.firstSlice.modality !== Modality.MR) {
            return [];
        }
        return MR_AUTO_WL_ALGORITHMS.map((algo) => {
            const wl = algo.compute(
                this.floatPixelData,
                this.grid.sizeX,
                this.grid.sizeY,
                this.grid.sizeZ
            );
            return {
                name: algo.name,
                ww: wl.window,
                wc: wl.level,
            };
        });
    }

    public getDefaultWindowLevel(): WindowLevel {
        return (
            this.windowLevel ?? // first try user-set WL to keep settings persistent across the workflows
            this.getAcquisitionWindowLevel() ??
            this.getAutoWindowLevel() ??
            new WindowLevel(400, 20) // default conventional WL values
        );
    }

    /**
     * Find the patient-space coordinates of the voxel with maximum intensity.
     */
    public findMaxIntensityPosition(): ReadonlyVec3 | null {
        const data = this.floatPixelData;
        if (data.length === 0) {
            return null;
        }

        let maxVal = -Infinity;
        let maxIdx = 0;

        for (let i = 0; i < data.length; i++) {
            if (data[i] > maxVal) {
                maxVal = data[i];
                maxIdx = i;
            }
        }

        return this.grid.indexToVoxelCenterWorldSpace(maxIdx);
    }

    public constructor(
        grid: Grid,
        dicomUid: string,
        forUid: string,
        public readonly firstSlice: DicomImageSlice, // TODO: remove this, needed now to construct the ImageObject,
        series: Series,
        interpolationInfo?: InterpolationInfo
    ) {
        this._series = series;
        // MR images often have different WC/WW per slice;
        // use median for robustness. Other modalities use first slice.
        this._acquisitionWindowLevel =
            firstSlice.modality === Modality.MR
                ? this.computeMedianAcquisitionWindowLevel(series)
                : firstSlice.windowLevel;
        this._interpolationInfo = interpolationInfo;

        this.grid = grid;
        this.dicomUid = dicomUid;
        this.frameOfReferenceUid = forUid;
        this._pixelData = new Int16Array(0);
    }

    private readonly _series: Series;

    private readonly _acquisitionWindowLevel: WindowLevel | undefined;

    /**
     * Compute the acquisition window level as the median WC/WW
     * across all slices. MR images often have different WC/WW
     * values per slice; using the median is more robust than
     * picking the first slice.
     */
    private computeMedianAcquisitionWindowLevel(
        series: Series
    ): WindowLevel | undefined {
        const slices = series.dicomImageSlices;
        const windows: number[] = [];
        const levels: number[] = [];
        for (const slice of slices) {
            if (slice.windowLevel !== undefined) {
                windows.push(slice.windowLevel.window);
                levels.push(slice.windowLevel.level);
            }
        }
        if (windows.length === 0) {
            return undefined;
        }
        windows.sort((a, b) => a - b);
        levels.sort((a, b) => a - b);
        const mid = Math.floor(windows.length / 2);
        const medianWindow =
            windows.length % 2 === 1
                ? windows[mid]
                : Math.round((windows[mid - 1] + windows[mid]) / 2);
        const medianLevel =
            levels.length % 2 === 1
                ? levels[mid]
                : Math.round((levels[mid - 1] + levels[mid]) / 2);
        return new WindowLevel(Math.max(1, medianWindow), medianLevel);
    }

    private readonly _interpolationInfo: InterpolationInfo | undefined;

    private _suvFactor: number | null | undefined = undefined;

    public get series(): Series {
        return this._series;
    }

    public get generationModel(): GenerationModelDetails | undefined {
        const seriesObject = this.series.seriesObjects.find(
            (x) => x.generationModel
        );
        return seriesObject?.generationModel;
    }

    public get isNew(): boolean {
        return this.series.seriesObjects.some((x) => x.isNew);
    }

    public get isExported(): boolean {
        return this.series.seriesObjects.some((x) => x.isExported);
    }

    public setExported(): void {
        this.series.setExported();
    }

    private _cachedMaxValue: number | undefined;

    public get maxValue(): number {
        if (this._cachedMaxValue !== undefined) {
            return this._cachedMaxValue;
        }
        let max = -Infinity;
        for (const d of this.floatPixelData) {
            if (d > max) {
                max = d;
            }
        }
        this._cachedMaxValue = max;
        return max;
    }

    private _cachedMinValue: number | undefined;

    public get minValue(): number {
        if (this._cachedMinValue !== undefined) {
            return this._cachedMinValue;
        }
        let min = Infinity;
        for (const d of this.floatPixelData) {
            if (d < min) {
                min = d;
            }
        }
        this._cachedMinValue = min;
        return min;
    }

    public get suvFactor(): number | null {
        if (this._suvFactor !== undefined) {
            return this._suvFactor;
        }
        if (
            this.firstSlice.modality !== Modality.PT ||
            !this.firstSlice.petMetadata
        ) {
            this._suvFactor = null;
        } else {
            this._suvFactor = computeSUVbwFactor(this.firstSlice.petMetadata);
        }
        return this._suvFactor;
    }

    public get patientWeightKg(): number | undefined {
        return this.firstSlice.petMetadata?.patientWeightKg;
    }

    public readonly grid: Grid;

    public readonly dicomUid: string;

    public readonly frameOfReferenceUid: string;

    private _windowLevel: WindowLevel | undefined;

    public get windowLevel(): WindowLevel | undefined {
        if (!this._windowLevel) {
            return undefined;
        }
        return this._windowLevel;
    }

    public set windowLevel(value: WindowLevel) {
        this._windowLevel = value;
        this.firstSlice.windowLevel = value;
    }

    private _pixelData: Int16Array;

    /**
     * Int16 pixel data for the full volume.
     * Not available for PET images — use {@link floatPixelData} instead.
     */
    public get pixelData(): Int16Array {
        if (this.hasWidePixelRange) {
            throw new Error(
                'Int16 pixel data is not available for PET images — ' +
                    'rescaled values exceed Int16 range. ' +
                    'Use floatPixelData.'
            );
        }
        if (this._pixelData.length === 0) {
            this._pixelData = new Int16Array(
                this.grid.sizeX * this.grid.sizeY * this.grid.sizeZ
            );
            if (this._interpolationInfo) {
                this.updatePixelDataWithInterpolation();
            } else {
                this.updatePixelDataFromSeries();
            }
        }
        return this._pixelData;
    }

    private _floatPixelData: Float32Array | null = null;

    /**
     * Float32 representation of the pixel data, suitable for GPU upload
     * and value lookups.
     *
     * - PET: assembled directly from DICOM slice Float32 data — no Int16
     *   intermediate, no overflow/correction needed.
     * - CT/MR: widened from the Int16 volume data.
     *
     * Built lazily on first access and cached for the lifetime of the
     * Image3D instance. ImageObject takes a reference to this array
     * rather than allocating its own copy.
     */
    public get floatPixelData(): Float32Array {
        if (this._floatPixelData !== null) {
            return this._floatPixelData;
        }

        if (this.hasWidePixelRange) {
            // PET: assemble Float32 directly from DICOM — no Int16
            // intermediate
            if (this._interpolationInfo) {
                throw new Error(
                    'Slice interpolation is not supported ' + 'for PET images.'
                );
            }
            this.updateFloatPixelDataFromSeries();
            return this._floatPixelData!;
        }

        // CT/MR: widen from Int16
        const int16Data = this.pixelData;
        const float32 = new Float32Array(int16Data.length);
        for (let i = 0; i < int16Data.length; i++) {
            float32[i] = int16Data[i];
        }
        this._floatPixelData = float32;
        return this._floatPixelData;
    }

    /**
     * Whether the rescaled pixel values can exceed the Int16 range.
     * True for PET images where per-slice RescaleSlope causes values
     * above 32767 to wrap into negative Int16 territory.
     */
    public get hasWidePixelRange(): boolean {
        return this.firstSlice.modality === Modality.PT;
    }

    /** Whether this image contains interpolated slices */
    public get hasInterpolatedSlices(): boolean {
        return this._interpolationInfo !== undefined;
    }

    /** Number of slices that were interpolated (0 if no interpolation) */
    public get interpolatedSliceCount(): number {
        if (!this._interpolationInfo) {
            return 0;
        }
        return (
            this._interpolationInfo.totalSlices -
            this._interpolationInfo.sliceMap.size
        );
    }

    /**
     * Update pixel data with interpolation for missing slices
     */
    private updatePixelDataWithInterpolation(): void {
        const info = this._interpolationInfo!;
        const sliceSize = this.grid.sizeX * this.grid.sizeY;

        // Build SliceInfo array for interpolation lookups
        const slices: SliceInfo[] = [];
        for (const [zIndex, slice] of info.sliceMap) {
            slices.push({ zIndex, pixelData: slice.pixelData });
        }

        // Fill all slices
        for (let z = 0; z < info.totalSlices; z++) {
            const pixelIdx = z * sliceSize;

            if (info.sliceMap.has(z)) {
                // Real slice - copy data directly
                this._pixelData.set(info.sliceMap.get(z)!.pixelData, pixelIdx);
            } else {
                // Find nearest slices for interpolation
                const [below, above] = findNearestSlices(z, slices);
                if (above.zIndex === below.zIndex) {
                    throw new Error(
                        'Cannot interpolate between identical z-indices'
                    );
                }
                // Missing slice - interpolate from neighbors
                const weight =
                    (z - below.zIndex) / (above.zIndex - below.zIndex);

                const interpolated = interpolateSlice(
                    below.pixelData,
                    above.pixelData,
                    weight,
                    info.interpolationMethod
                );
                this._pixelData.set(interpolated, pixelIdx);
            }
        }
    }

    public get seriesUid(): string {
        return this.series.seriesInstanceUID;
    }

    public get studyUid(): string {
        return this.firstSlice.studyInstanceUID;
    }

    public get length(): number {
        return this.grid.sizeZ;
    }

    public get seriesMetadata(): DicomSeries {
        return this.series.seriesModule;
    }

    public get sopClassUid(): string {
        return this.firstSlice.datasetWrapper.sopClassUID;
    }

    public get imageSlices(): DicomImageSlice[] {
        return this.series.dicomImageSlices;
    }

    /// Returns an iterator to get the slices as DICOM blobs
    public *getSlicesAsDicomBlobs(): IterableIterator<Blob> {
        for (const slice of this.series.dicomImageSlices) {
            yield slice.getAsDicomBlob();
        }
    }
}
